// src/hooks/useLogs.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';
import useAlert from './useAlert';

const LOGS_KEY = 'logs';

export const useLogs = (filters = {}) => {
    return useQuery({
        queryKey: [LOGS_KEY, 'lista', filters],
        queryFn: async () => {
            const { data } = await apiClient.get('/logs', { params: filters });
            return {
                data: data.data || [],
                count: data.count || 0,
                total: data.total || 0,
                pagination: data.pagination || {},
            };
        },
        staleTime: 1000 * 30,
    });
};

export const useLogById = (id) => {
    return useQuery({
        queryKey: [LOGS_KEY, 'detalhe', id],
        queryFn: async () => {
            const { data } = await apiClient.get(`/logs/${id}`);
            return data.data || null;
        },
        enabled: !!id,
        staleTime: 1000 * 60,
    });
};

export const useLogsEstatisticas = (params = {}) => {
    return useQuery({
        queryKey: [LOGS_KEY, 'estatisticas', params],
        queryFn: async () => {
            const { data } = await apiClient.get('/logs/estatisticas', { params });
            return data.data || {};
        },
        staleTime: 1000 * 60 * 5,
    });
};

export const useLogsDashboard = (periodo = '24h') => {
    return useQuery({
        queryKey: [LOGS_KEY, 'dashboard', periodo],
        queryFn: async () => {
            const { data } = await apiClient.get('/logs/dashboard', { params: { periodo } });
            return data.data || {};
        },
        staleTime: 1000 * 60 * 2,
        refetchInterval: 1000 * 60 * 5,
    });
};

export const useModulosAtivos = () => {
    return useQuery({
        queryKey: [LOGS_KEY, 'modulos'],
        queryFn: async () => {
            const { data } = await apiClient.get('/logs/modulos/ativos');
            return data.data || [];
        },
        staleTime: 1000 * 60 * 10,
    });
};

export const useMutateLogs = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const limparAntigos = useMutation({
        mutationFn: (params) => apiClient.delete('/logs/limpar/antigos', { params }),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: [LOGS_KEY] });
            const removidos = res.data?.data?.removidos ?? 0;
            setAlert(`${removidos} log(s) antigo(s) removido(s) com sucesso.`, 'success');
        },
        onError: (e) => setAlert(e.response?.data?.error || 'Erro ao limpar logs.', 'danger'),
    });

    const exportarCSV = async (params = {}) => {
        const response = await apiClient.get('/logs/exportar/csv', {
            params,
            responseType: 'blob',
        });
        const url = URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `logs_${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return { limparAntigos, exportarCSV };
};
